import { Select } from '../ui';
import { cn } from '../../utils/cn';
import { colorForRetailer, formatCount } from '../production/chartTheme';
import { ALL_RETAILERS, HISTORY_RANGES, retailerKey } from './spotHistoryModel';

/**
 * Toolbar for the spot history modal.
 *
 * The retailer pills double as the color legend (Tableau shows a radio list plus
 * a separate legend; here it's one control). Changing retailer filters
 * client-side; changing the range refetches — that's the parent's call.
 */
export default function SpotHistoryFilters({
  retailers,
  totalSpots,
  retailer,
  onRetailerChange,
  range,
  onRangeChange,
  disabled = false,
}) {
  const isSelected = (value) => (
    value === ALL_RETAILERS
      ? retailer === ALL_RETAILERS
      : retailer !== ALL_RETAILERS && retailerKey(retailer) === retailerKey(value)
  );

  return (
    <div className="flex flex-col lg:flex-row lg:items-end gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-neutral-500 mb-1.5">Retailer</div>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Retailer">
          <RetailerPill
            label="All"
            count={totalSpots}
            selected={isSelected(ALL_RETAILERS)}
            onClick={() => onRetailerChange(ALL_RETAILERS)}
            disabled={disabled}
          />
          {retailers.map((option) => (
            <RetailerPill
              key={option.value}
              label={option.label}
              count={option.count}
              color={colorForRetailer(option.value)}
              selected={isSelected(option.value)}
              onClick={() => onRetailerChange(option.value)}
              disabled={disabled}
            />
          ))}
        </div>
      </div>

      <Select
        label="Start date"
        className="w-full lg:w-48 shrink-0"
        value={range}
        onChange={(e) => onRangeChange(e.target.value)}
        options={HISTORY_RANGES.map(({ value, label }) => ({ value, label }))}
      />
    </div>
  );
}

function RetailerPill({ label, count, color, selected, onClick, disabled }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
        'transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        selected
          ? 'bg-primary-50 border-primary-500 text-primary-800'
          : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50',
        count === 0 && !selected && 'opacity-60'
      )}
    >
      {color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />}
      {label}
      <span className="tabular-nums text-neutral-500">{formatCount(count)}</span>
    </button>
  );
}
